import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  // Convert number to string and handle decimals
  const numStr = Math.abs(amount).toFixed(0);
  
  // Split integer into parts based on Indian numbering system
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNums = numStr.substring(0, numStr.length - 3);
  
  // Add commas according to Indian number system
  const formatted = otherNums !== '' 
    ? otherNums.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree 
    : lastThree;
  
  return '₹' + (amount < 0 ? '-' : '') + formatted;
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const EbitdaCalculator = () => {
  const [values, setValues] = useState({
    revenue: 10000000,
    costOfGoodsSold: 6000000,
    operatingExpenses: 2000000,
    depreciation: 500000,
    amortization: 200000,
    interestExpense: 300000,
    taxes: 400000
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [showToolTip, setShowToolTip] = useState('');
  
  const handleChange = (key, value) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateEbitda = () => {
    const { 
      revenue, 
      costOfGoodsSold, 
      operatingExpenses,
      depreciation,
      amortization,
      interestExpense,
      taxes
    } = values;
    
    // Calculate Gross Profit
    const grossProfit = revenue - costOfGoodsSold;
    const grossProfitMargin = (grossProfit / revenue) * 100;
    
    // Calculate Operating Income/EBIT
    const operatingIncome = grossProfit - operatingExpenses;
    const operatingMargin = (operatingIncome / revenue) * 100;
    
    // Calculate EBITDA
    const ebitda = operatingIncome + depreciation + amortization;
    const ebitdaMargin = (ebitda / revenue) * 100;
    
    // Calculate Net Income
    const netIncome = operatingIncome - interestExpense - taxes;
    const netProfitMargin = (netIncome / revenue) * 100;
    
    // Industry comparisons (these would typically come from an external source)
    // Using sample values for demonstration
    const industryEbitdaMargin = 12.5; // Sample value
    const performanceVsIndustry = ebitdaMargin - industryEbitdaMargin;
    
    // Calculate Enterprise Value (EV) using a sample EBITDA multiple
    // This would typically be based on industry or company specifics
    const ebitdaMultiple = 8; // Sample value
    const enterpriseValue = ebitda * ebitdaMultiple;
    
    // Calculate Interest Coverage Ratio
    const interestCoverageRatio = interestExpense > 0 ? ebitda / interestExpense : Infinity;
    
    return {
      grossProfit,
      grossProfitMargin,
      operatingIncome,
      operatingMargin,
      ebitda,
      ebitdaMargin,
      netIncome,
      netProfitMargin,
      industryEbitdaMargin,
      performanceVsIndustry,
      enterpriseValue,
      interestCoverageRatio
    };
  };

  const [result, setResult] = useState(calculateEbitda());

  useEffect(() => {
    setResult(calculateEbitda());
  }, [values]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">EBITDA Margin Calculator</h1>
        <h2 className="text-lg text-gray-600">Calculate and analyze your company's EBITDA and profitability metrics</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Revenue & Costs */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue & Direct Costs</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Revenue
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('revenue')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'revenue' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Total income from sales before any deductions
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.revenue}
                    onChange={(e) => handleChange('revenue', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={100000}
                    max={100000000}
                    step={100000}
                    value={values.revenue}
                    onChange={(e) => handleChange('revenue', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1L</span>
                    <span>₹10Cr</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Cost of Goods Sold
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('cogs')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'cogs' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Direct costs attributable to the production of goods sold
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.costOfGoodsSold}
                    onChange={(e) => handleChange('costOfGoodsSold', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={values.revenue}
                    step={10000}
                    value={values.costOfGoodsSold}
                    onChange={(e) => handleChange('costOfGoodsSold', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>{formatCurrency(values.revenue)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Operating Expenses */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Operating Expenses</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Operating Expenses
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('opex')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'opex' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          General and administrative expenses not directly tied to production
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.operatingExpenses}
                    onChange={(e) => handleChange('operatingExpenses', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={values.revenue - values.costOfGoodsSold}
                    step={10000}
                    value={values.operatingExpenses}
                    onChange={(e) => handleChange('operatingExpenses', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>{formatCurrency(values.revenue - values.costOfGoodsSold)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* EBITDA Components */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">EBITDA Components</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Depreciation
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('depreciation')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'depreciation' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Allocation of tangible asset costs over useful life
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.depreciation}
                    onChange={(e) => handleChange('depreciation', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={1000000}
                    step={10000}
                    value={values.depreciation}
                    onChange={(e) => handleChange('depreciation', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>₹10L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Amortization
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('amortization')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'amortization' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Allocation of intangible asset costs over useful life
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.amortization}
                    onChange={(e) => handleChange('amortization', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={1000000}
                    step={10000}
                    value={values.amortization}
                    onChange={(e) => handleChange('amortization', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>₹10L</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Additional Financial Details</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Interest Expense
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('interest')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'interest' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Cost of borrowing money (loan interest payments)
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={values.interestExpense}
                        onChange={(e) => handleChange('interestExpense', Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={1000000}
                        step={10000}
                        value={values.interestExpense}
                        onChange={(e) => handleChange('interestExpense', Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>₹0</span>
                        <span>₹10L</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Taxes
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('taxes')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'taxes' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Income taxes for the period
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={values.taxes}
                        onChange={(e) => handleChange('taxes', Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={1000000}
                        step={10000}
                        value={values.taxes}
                        onChange={(e) => handleChange('taxes', Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>₹0</span>
                        <span>₹10L</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">EBITDA Analysis</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.ebitda)}
                  </div>
                  <div className="text-sm text-gray-300">EBITDA</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">EBITDA Margin</span>
                    <span className="font-bold">{result.ebitdaMargin.toFixed(2)}%</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Gross Profit</span>
                    <span className="font-bold">{formatCurrency(result.grossProfit)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Gross Profit Margin</span>
                    <span className="font-bold">{result.grossProfitMargin.toFixed(2)}%</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Operating Income (EBIT)</span>
                    <span className="font-bold">{formatCurrency(result.operatingIncome)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Operating Margin</span>
                    <span className="font-bold">{result.operatingMargin.toFixed(2)}%</span>
                  </div>

                  {showAdvanced && (
                    <>
                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Net Income</span>
                        <span className="font-bold">{formatCurrency(result.netIncome)}</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Net Profit Margin</span>
                        <span className="font-bold">{result.netProfitMargin.toFixed(2)}%</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Interest Coverage Ratio</span>
                        <span className="font-bold">{result.interestCoverageRatio === Infinity ? '∞' : result.interestCoverageRatio.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Generate Financial Report →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EbitdaCalculator;